import { mergeProps } from '@creator/ui/utils/merge-props/merge-props';
import ExternalLinkPreview, { type ExternalLinkPreviewProps } from '@src/basic-components/ExternalLinkPreview/ExternalLinkPreview';
import { cleanHtmlToPlainText } from '@src/utils/html-utils/html-utils';
import { isObjectEmpty } from '@src/utils/object-utils/object-utils';
import { ensureHttpProtocol, getUrlHostname, prettyUrl } from '@src/utils/url-utils/url-utils';
import { type FC, memo, useMemo } from 'react';

type Meta = {
    image?: { url: string };
    description?: string;
    title?: string;
    url?: string;
};

type BlockData = {
    meta: Meta;
    link: string;
};

export interface EditorViewOnlyLinkToolProps {
    className?: string;
    blockData: BlockData;
    enablePreview?: boolean;
    externalLinkPreviewProps?: Partial<ExternalLinkPreviewProps>;
}

const EditorViewOnlyLinkTool: FC<EditorViewOnlyLinkToolProps> = props => {
    const { enablePreview = true, blockData, className, externalLinkPreviewProps = {} } = props;
    const { meta, link } = blockData;

    const image = useMemo(() => meta?.image?.url || '', [meta?.image?.url]);

    const url = useMemo(() => meta.url || ensureHttpProtocol(link), [meta.url, link]);

    const title = useMemo(() => cleanHtmlToPlainText(meta?.title || ''), [meta?.title]);

    const description = useMemo(() => cleanHtmlToPlainText(meta?.description || ''), [meta?.description]);

    const hostname = useMemo(() => getUrlHostname(url), [url]);

    const mergedExternalLinkPreviewProps = useMemo(
        () =>
            mergeProps<[ExternalLinkPreviewProps, Partial<ExternalLinkPreviewProps>]>(
                {
                    caption: description,
                    image,
                    title,
                    hostname,
                    url,
                },
                externalLinkPreviewProps
            ),
        [description, image, title, hostname, url, externalLinkPreviewProps]
    );

    function renderContent() {
        if (!enablePreview) return <div className="break-words">{window.decodeURI(link)}</div>;
        if (!meta || isObjectEmpty(meta))
            return (
                <a className="break-words" target="_blank" href={ensureHttpProtocol(link)} rel="noreferrer">
                    {prettyUrl(link)}
                </a>
            );

        return <ExternalLinkPreview {...mergedExternalLinkPreviewProps} />;
    }

    return <div className={`link-tool ${className}`}>{renderContent()}</div>;
};

export default memo(EditorViewOnlyLinkTool);
